import React, { useEffect, useMemo, useState } from "react";
import { Combobox } from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { A } from "../styles/appleClasses";

export type AppleComboBoxProps<T> = {
  label?: string;
  options: T[];
  value: T | null;
  onChange: (val: T | null) => void;
  getOptionLabel: (opt: T) => string;
  getOptionSearchText?: (opt: T) => string;
  filterOptions?: (options: T[], query: string) => T[];
  renderOption?: (opt: T) => React.ReactNode;
  placeholder?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  disabled?: boolean;
  className?: string;
  emptyText?: string;
  toggleOptionsLabel?: string;
  preserveQueryOnDisplayChange?: boolean;
};

export function AppleComboBox<T extends { id: string | number }>({
  label,
  options,
  value,
  onChange,
  getOptionLabel,
  getOptionSearchText,
  filterOptions,
  renderOption,
  placeholder = "Select…",
  inputProps = {},
  disabled = false,
  className = "",
  emptyText = "No results",
  toggleOptionsLabel = "Toggle options",
  preserveQueryOnDisplayChange = false,
}: AppleComboBoxProps<T>) {
  const [query, setQuery] = useState("");
  const [editingQuery, setEditingQuery] = useState(false);
  const [isMobileLike, setIsMobileLike] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(pointer: coarse)");

    const apply = () => {
      setIsMobileLike(mq.matches || window.innerWidth < 768);
    };

    apply();

    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", apply);
      window.addEventListener("resize", apply);
      return () => {
        mq.removeEventListener("change", apply);
        window.removeEventListener("resize", apply);
      };
    }

    mq.addListener?.(apply);
    window.addEventListener("resize", apply);
    return () => {
      mq.removeListener?.(apply);
      window.removeEventListener("resize", apply);
    };
  }, []);

  const filtered = useMemo(() => {
    if (!query) return options;
    if (filterOptions) return filterOptions(options, query);
    const q = query.toLowerCase();
    return options.filter((opt) =>
      (getOptionSearchText?.(opt) ?? getOptionLabel(opt))
        .toLowerCase()
        .includes(q),
    );
  }, [options, query, filterOptions, getOptionSearchText, getOptionLabel]);

  const displayValue = (item: unknown) => {
    if (preserveQueryOnDisplayChange && editingQuery) return query;
    if (!item) return "";
    if (typeof item === "object" && item !== null && "id" in item) {
      return getOptionLabel(item as T);
    }
    return typeof item === "string" ? item : "";
  };

  const {
    className: inputClassName,
    onChange: inputOnChange,
    onBlur: inputOnBlur,
    ...restInputProps
  } = inputProps;

  return (
    <Combobox
      value={value}
      onChange={(v) => {
        onChange(v);
        setQuery("");
        setEditingQuery(false);
      }}
      onClose={() => {
        setQuery("");
        setEditingQuery(false);
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }}
      nullable
      disabled={disabled}
    >
      {({ open }) => (
        <div className={className}>
          {label && (
            <Combobox.Label className={clsx("mb-1 block", A.label)}>
              {label}
            </Combobox.Label>
          )}

          <div
            className={clsx(
              "relative overflow-visible",
              open ? "z-[140]" : "z-0",
            )}
          >
            <Combobox.Input
              {...restInputProps}
              className={clsx(
                A.inputBase,
                "pr-11",
                disabled && A.fieldDisabled,
                inputClassName,
              )}
              displayValue={displayValue}
              onChange={(e) => {
                setQuery(e.target.value);
                setEditingQuery(true);
                inputOnChange?.(e);
              }}
              onBlur={(e) => {
                inputOnBlur?.(e);
              }}
              placeholder={placeholder}
              autoComplete="off"
              disabled={disabled}
            />

            <Combobox.Button
              className={clsx(
                "absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-xl bg-transparent text-gray-400",
                disabled ? "cursor-not-allowed" : "cursor-pointer",
              )}
              disabled={disabled}
              aria-label={toggleOptionsLabel}
              type="button"
            >
              <ChevronUpDownIcon className="h-5 w-5" />
            </Combobox.Button>

            <Combobox.Options
              unmount={false}
              modal={false}
              className="absolute left-0 top-full z-[150] mt-2 overflow-hidden rounded-2xl border border-gray-200 bg-white p-1 shadow-md focus:outline-none"
              style={{
                minWidth: "100%",
                width: "100%",
                maxWidth: "min(calc(100vw - 1rem), 28rem)",
              }}
            >
              <div
                className="overflow-y-auto overscroll-contain [touch-action:pan-y]"
                style={{
                  WebkitOverflowScrolling: "touch",
                  maxHeight: isMobileLike ? "40vh" : "13rem",
                }}
              >
                {filtered.length === 0 ? (
                  <div className={clsx("px-3 py-2 text-gray-500", A.body)}>
                    {emptyText}
                  </div>
                ) : (
                  filtered.map((opt) => (
                    <Combobox.Option
                      key={String(opt.id)}
                      value={opt}
                      className={({ active }) =>
                        clsx(
                          "cursor-pointer select-none rounded-xl px-3 py-2",
                          A.body,
                          active
                            ? "bg-gray-100 text-gray-700"
                            : "text-gray-700",
                        )
                      }
                    >
                      {({ selected }) => (
                        <div className="flex items-start justify-between gap-3">
                          <span className="block min-w-0 flex-1 whitespace-normal break-words">
                            {renderOption ? renderOption(opt) : getOptionLabel(opt)}
                          </span>
                          {selected && (
                            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-700" />
                          )}
                        </div>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </div>
            </Combobox.Options>
          </div>
        </div>
      )}
    </Combobox>
  );
}
